import React, { useEffect, useState } from 'react';
import { X, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.7 34.6 27 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.6 5.6C41.4 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

const SignInModal = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleContinue = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast({ title: 'Enter your email', description: 'Please provide an email address or username to continue.' });
      return;
    }
    toast({ title: 'Almost there', description: 'Authentication is not wired up in this preview build.' });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <button onClick={onClose} className="absolute top-5 right-5 h-10 w-10 rounded-full bg-[#15151d] border border-[#2a2a33] flex items-center justify-center text-[#9a9aa4] hover:text-white transition-colors z-10">
        <X className="h-5 w-5" />
      </button>
      <div className="relative w-full max-w-[400px] animate-fade-up">
        <div className="rounded-2xl bg-[#101017] border border-[#22222b] p-8 shadow-2xl">
          <h2 className="font-serif-display text-[27px] leading-tight text-white text-center">Sign in to Steno Pace Master</h2>
          <p className="text-center text-[13px] text-[#8a8a94] mt-2">Welcome back! Please sign in to continue</p>

          <button
            onClick={() => toast({ title: 'Google Sign-In', description: 'Social login is not enabled in this preview build.' })}
            className="mt-6 w-full flex items-center justify-center gap-2.5 py-2.5 rounded-lg bg-[#16161d] border border-[#2a2a33] text-[14px] text-white hover:bg-[#1b1b23] transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-[#22222b]" />
            <span className="text-[12px] text-[#6a6a74]">or</span>
            <div className="h-px flex-1 bg-[#22222b]" />
          </div>

          <form onSubmit={handleContinue}>
            <label className="block text-[12.5px] font-semibold text-[#c9c9d0] mb-2">Email address or username</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email or username"
              className="w-full px-3.5 py-2.5 rounded-lg bg-[#0b0b11] border border-[#2a2a33] text-[14px] text-white placeholder:text-[#5a5a64] outline-none focus:border-[#c99a2e]/60 transition-colors"
            />
            <button type="submit" className="mt-4 w-full flex items-center justify-center gap-1 py-2.5 rounded-lg bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500] text-[14px] font-semibold hover:from-[#f0d27a] hover:to-[#e0b84c] transition-all">
              Continue <ChevronRight className="h-4 w-4" />
            </button>
          </form>

          <p className="text-center text-[13px] text-[#8a8a94] mt-6">
            Don't have an account? <span className="text-[#e0b84c] font-semibold cursor-pointer hover:underline">Sign up</span>
          </p>
        </div>
        <div className="text-center py-3 text-[11px] text-[#5a5a64]">Secured by Steno Pace Master</div>
      </div>
    </div>
  );
};

export default SignInModal;
